import { createAsyncThunk } from '@reduxjs/toolkit'
import download from 'downloadjs'
import { apiGET, REQUEST_METHOD } from 'utils/apiClient'
import { getMimeType } from 'utils/mimeType'
import { APIRoute } from 'routes/api'
import { createApiThunk } from 'utils/thunkWrapper'
import {
  getChallenge as getChallengeApi,
  createChallenge as createChallengeApi,
  updateChallengeV2 as updateChallengeApi,
  transitionChallengeState,
  deleteChallenge as deleteChallengeApi,
  addParticipants as addParticipantsApi,
  cloneChallenge as cloneChallengeApi,
  overrideRoundEligibility as overrideRoundEligibilityApi,
} from 'api/challenges-v2'
import {
  ChallengeCategory,
  CloneChallengeResponse,
  type AddParticipantsResponse,
  type CreateChallengeResponse,
  type DeleteChallengeResponse,
  type GetChallengeResponse,
  type TransitionChallengeStateResponse,
  type UpdateChallengeResponse,
  type OverrideRoundEligibilityResponse,
  type OverrideRoundEligibilityPayload,
} from 'api/types'
import { getChallengeLeaderboard } from 'store/leaderboard/thunks'
import { getChallenges } from 'store/challenges/thunks'
import { ChallengeComputedState } from 'utils/challenges'
import { getParticipantRounds } from 'store/participantRounds/thunks'

export const getChallenge = createApiThunk<GetChallengeResponse, string>({
  action: 'challenge',
  request: (id) => getChallengeApi(id),
})

export const getChallengeLeaderboardV1 = createApiThunk<ChallengeLeaderboardV1[], ChallengeLeaderboardPayload>({
  action: 'challenge/leaderboardV1',
  method: REQUEST_METHOD.GET,
  formatUrl: (payload) => APIRoute.GET_CHALLENGE_LEADERBOARD(payload.challengeId),
  getParams: (payload) => payload.params,
})

export const createNewChallenge = createApiThunk<CreateChallengeResponse, CreateChallengePayload>({
  action: 'challenge/create',
  request: (payload) => createChallengeApi(payload.data),
  getSuccessMessage: (payload) => payload.successMessage,
})

export const cloneChallenge = createApiThunk<CloneChallengeResponse, string>({
  action: 'challenge/clone',
  request: (id) => cloneChallengeApi(id) as any,
  getSuccessMessage: () => 'Challenge duplicated successfully',
  onSuccess(payload, response, thunkApi) {
    thunkApi.dispatch(
      getChallenges({
        category: [ChallengeCategory.SPONSORED],
        challengeComputedState: [
          ChallengeComputedState.DRAFT,
          ChallengeComputedState.LIVE,
          ChallengeComputedState.UNPUBLISHED,
          ChallengeComputedState.UPCOMING,
          ChallengeComputedState.VISIBLE,
        ],
      }),
    )
  },
})

export const updateChallenge = createApiThunk<UpdateChallengeResponse, UpdateChallengePayload>({
  action: 'challenge/update',
  request: (payload) =>
    updateChallengeApi(payload.challengeId, {
      payload: { ...payload.data },
      updateMask: Object.keys(payload.data)
        .filter((key) => key !== 'category')
        .join(','),
    }),
  getSuccessMessage: (payload) => payload.successMessage,
})

// Changing the controlled state is a separate endpoint from updating the challenge itself.
// It is idempotent, but the backend only allows draft -> published, published -> unpublished
// and unpublished -> published, so callers have to check the current state before requesting one.
export const updateChallengeState = createApiThunk<TransitionChallengeStateResponse, UpdateChallengeStatePayload>({
  action: 'challenge/updateState',
  request: (payload) => transitionChallengeState(payload.challengeId, { controlledState: payload.controlledState }),
  getSuccessMessage: (payload) => payload.successMessage,
})

export const duplicateChallenge = createApiThunk<Challenge, string>({
  action: 'challenge/duplicate',
  method: REQUEST_METHOD.POST,
  formatUrl: (id) => APIRoute.POST_DUPLICATE_CHALLENGE(id),
})

export const deleteChallenge = createApiThunk<DeleteChallengeResponse, string>({
  action: 'challenge/delete',
  request: (id) => deleteChallengeApi(id),
})

export const uploadChallengeImage = createApiThunk<ChallengeImageResponse, ChallengeImagePayload>({
  action: 'challenge/uploadImage',
  method: REQUEST_METHOD.POST,
  contentType: 'multipart/form-data',
  formatUrl: () => APIRoute.POST_CHALLENGE_IMAGE,
  getData: (payload) => payload.data,
})

export const deleteChallengePlayer = createApiThunk<void, DeleteChallengePlayerPayload>({
  action: 'challenge/deletePlayer',
  method: REQUEST_METHOD.DELETE,
  formatUrl: (payload) => APIRoute.DELETE_CHALLENGE_PLAYER(payload.challengeId, payload.participantId),
  onSuccess: (payload, response, thunkApi) => {
    const data = {
      challengeId: payload.challengeId,
      params: { limit: payload.limit },
    }
    thunkApi.dispatch(getChallengeLeaderboardV1(data))
    thunkApi.dispatch(getChallenge(payload.challengeId))
  },
})

export const addChallengePlayer = createApiThunk<AddParticipantsResponse, AddChallengePlayerPayload>({
  action: 'challenge/addPlayer',
  request: (payload) =>
    addParticipantsApi(payload.challengeId, {
      userIds: [payload.playerId],
      forceAccept: payload.category === ChallengeCategory.SPONSORED ? true : undefined,
    }),
  onSuccess: (payload, response, thunkApi) => {
    const data = {
      challengeId: payload.challengeId,
      params: { limit: payload.limit },
    }
    thunkApi.dispatch(getChallengeLeaderboard(data))
    thunkApi.dispatch(getChallenge(payload.challengeId))
  },
})

export const getChallengeLeaderboardReportV1 = createAsyncThunk<any, string, { rejectValue: APIError }>(
  'challenge/leaderboardReportV1',
  async (challengeId, thunkApi) => {
    try {
      const response = await apiGET({
        url: APIRoute.GET_CHALLENGE_LEADERBOARD_REPORT(challengeId),
        contentType: getMimeType('xlsx'),
      })
      return download(response.data, 'Challenge results', getMimeType('xlsx'))
    } catch (error: any) {
      return thunkApi.rejectWithValue(error.response.data.error)
    }
  },
)

export const overrideRoundEligibility = createApiThunk<
  OverrideRoundEligibilityResponse,
  { challengeId: string; participantId: string; roundId: string; body?: OverrideRoundEligibilityPayload }
>({
  action: 'challenge/override',
  request: (payload) =>
    overrideRoundEligibilityApi(payload.challengeId, payload.participantId, payload.roundId, payload?.body),
  onSuccess: async (payload, response, thunkApi) => {
    thunkApi.dispatch(getParticipantRounds({ challengeId: payload.challengeId, participantId: payload.participantId }))
    thunkApi.dispatch(
      getChallengeLeaderboard({
        challengeId: payload.challengeId,
        params: { limit: 100 },
      }),
    )
  },
  getSuccessMessage: (payload) =>
    payload.body?.override === 'ROUND_ELIGIBILITY_OVERRIDE_EXCLUDE'
      ? 'Round was excluded succesfully'
      : 'Round was included succesfully',
})
